/**
 * HtmlEditor · Editor rich-text ligero contentEditable
 * Toolbar: Negrita, Cursiva, Subrayado, Lista viñeta, Lista numerada, Link, Limpiar
 * No dependencias externas — usa document.execCommand para máxima compatibilidad.
 */
import { useRef, useEffect, useState } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Eraser } from "lucide-react";

export default function HtmlEditor({ value, onChange, placeholder, testId, minRows = 5 }) {
  const editorRef = useRef(null);
  const [focused, setFocused] = useState(false);

  // Sincronizar valor externo → DOM sólo si el editor NO tiene foco (evita perder caret)
  useEffect(() => {
    if (!editorRef.current) return;
    if (document.activeElement === editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange?.(editorRef.current?.innerHTML || "");
  };

  const handleInput = () => {
    onChange?.(editorRef.current?.innerHTML || "");
  };

  const insertLink = () => {
    const url = window.prompt("URL:", "https://");
    if (url) exec("createLink", url);
  };

  const btn = "p-1.5 rounded hover:bg-gray-100 text-gray-600 hover:text-[#0A3747] transition";

  return (
    <div className={`border rounded-md overflow-hidden bg-white transition ${focused ? "ring-2 ring-[#E0E621]/40 border-[#E0E621]" : "border-gray-300"}`}>
      <div className="flex items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-1 py-1">
        <button type="button" onClick={() => exec("bold")} className={btn} title="Negrita (Ctrl+B)"><Bold size={14} /></button>
        <button type="button" onClick={() => exec("italic")} className={btn} title="Cursiva (Ctrl+I)"><Italic size={14} /></button>
        <button type="button" onClick={() => exec("underline")} className={btn} title="Subrayado (Ctrl+U)"><Underline size={14} /></button>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <button type="button" onClick={() => exec("insertUnorderedList")} className={btn} title="Lista con viñetas"><List size={14} /></button>
        <button type="button" onClick={() => exec("insertOrderedList")} className={btn} title="Lista numerada"><ListOrdered size={14} /></button>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <button type="button" onClick={insertLink} className={btn} title="Insertar enlace"><LinkIcon size={14} /></button>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <button type="button" onClick={() => exec("removeFormat")} className={btn} title="Limpiar formato"><Eraser size={14} /></button>
        <span className="ml-auto text-[10px] font-mono text-gray-400 pr-2">HTML</span>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        data-testid={testId}
        data-placeholder={placeholder}
        className="html-editor px-3 py-2 text-sm outline-none focus:outline-none prose prose-sm max-w-none"
        style={{ minHeight: `${minRows * 22}px` }}
        suppressContentEditableWarning
      />
      <style>{`
        .html-editor:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          font-style: italic;
        }
        .html-editor ul { list-style: disc; padding-left: 1.5rem; }
        .html-editor ol { list-style: decimal; padding-left: 1.5rem; }
        .html-editor a { color: #0A3747; text-decoration: underline; }
      `}</style>
    </div>
  );
}
